import React from "react";
import { notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";
import { useGlobal } from "../../context/global-context";
import WineBottle from "../../components/wine-bottle";
import { inventory, money } from "./inventory";
import "../../styles/wine-theme.css";
import "./home.css";

export default function Home() {
  const { addToCart, globalState, wineStoreSettings } = useGlobal();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const minAge = wineStoreSettings?.wineAge ?? 21;

  const qtyInCart = (name) =>
    globalState.cart.find((c) => c.name === name)?.quantity || 0;
  const cartCount = globalState.cart.reduce((t, i) => t + i.quantity, 0);

  const handleAddToCart = (item) => {
    addToCart(item);
    notifications.show({
      title: "Added to cart",
      message: `${item.name} is in your cart`,
      color: "teal",
      icon: <IconCheck size={16} />,
      autoClose: 2000,
    });
  };

  return (
    <div className="vt-wine vt-page">
      <div className="vt-container vt-store">
        {/* Hero */}
        <section className="vt-hero">
          <div className="vt-hero__copy">
            <span className="vt-eyebrow vt-eyebrow--accent">Age restricted commerce</span>
            <h1>Wine, delivered with verified age at checkout.</h1>
            <p>A sandbox storefront showing how identity and age verification sit inside a real purchase flow.</p>
            {isMobile && (
              <div className="vt-hero__pill">
                <ShieldIcon />
                <span>{minAge}+ verified once per order</span>
              </div>
            )}
          </div>
          {!isMobile && (
            <div className="vt-hero__gate">
              <WineBottle width={66} dark />
              <div className="vt-hero__gate-text">
                <span className="vt-eyebrow">Gate</span>
                <strong>{minAge}+</strong>
                <span>Checked once<br />per order</span>
              </div>
            </div>
          )}
        </section>

        {/* Section header */}
        <div className="vt-section-head">
          <div>
            <span className="vt-eyebrow">The selection</span>
            <h2>Four bottles in stock</h2>
          </div>
          <span className="vt-mono vt-section-head__count">
            {String(inventory.length).padStart(2, "0")} / {String(inventory.length).padStart(2, "0")}
          </span>
        </div>

        {/* Product grid */}
        <div className="vt-grid">
          {inventory.map((item) => {
            const qty = qtyInCart(item.name);
            return (
              <article key={item.name} className={`vt-card vt-product${qty ? " vt-product--in-cart" : ""}`}>
                <div className="vt-product__art" style={{ background: item.tint }}>
                  {qty > 0 && <span className="vt-tag">In cart</span>}
                  <WineBottle fill={item.fill} width={isMobile ? 48 : 62} />
                </div>
                <div className="vt-product__body">
                  <div>
                    <span className="vt-eyebrow vt-product__kind">{item.kind}</span>
                    <div className="vt-product__row">
                      <h3>{item.name}</h3>
                      <span className="vt-price">${item.cost}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`vt-btn vt-btn--block${qty ? " vt-btn--primary" : ""}`}
                    onClick={() => handleAddToCart(item)}
                  >
                    {qty ? (isMobile ? `In cart · ${qty}` : `Added — ${qty} in cart`) : "Add to cart"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <footer className="vt-footer">
        <span>Demo environment. No payment is taken and no order ships.</span>
        <span className="vt-eyebrow">Age check at checkout</span>
      </footer>

      {cartCount > 0 && (
        <div className="vt-bottom-bar">
          <div className="vt-bottom-bar__total">
            <span className="vt-eyebrow">Cart</span>
            <span className="vt-mono">{money(globalState.cartTotal)}</span>
          </div>
          <button type="button" className="vt-btn vt-btn--primary vt-btn--lg" onClick={() => navigate("/checkout")}>
            Review cart
            <ArrowIcon />
          </button>
        </div>
      )}
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2.6 4.8 5.4v6c0 4.6 3 8.1 7.2 10 4.2-1.9 7.2-5.4 7.2-10v-6L12 2.6z" stroke="#00E5C7" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h9.4M9 4.6 12.6 8 9 11.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
